import React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { TaskPlayButton } from '../TaskPlayButton'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { TaskReactivateButton } from '../TaskReactivateButton'
import type { TaskEntryData } from './TaskEntry.types'

interface TaskEntryPlaySlotProps {
  task: TaskEntryData
  isExpanded: boolean
  showCollapsedPlay: boolean
  /** Gar kein Knopf, auch nicht aufgeklappt — siehe `TaskEntryProps.hidePlay`. */
  hidePlay?: boolean
  /** Mount-animation hints from the expand/collapse morph. */
  expandingFromCollapsed: boolean
  collapsingFromExpanded: boolean
  /** Lifted so the context menu can open the same Reactivate dropdown. */
  reactivateOpen: boolean
  setReactivateOpen: (open: boolean) => void
  inProjectContext: boolean
  onDirectWake: () => void
  onPlay?: (taskId: string) => void
  /** Collapsed Reactivate forwards the row click for selection. */
  onRowClick?: (taskId: string, event: React.MouseEvent) => void
}

/**
 * Trailing slot of the row — Play button by default, Reactivate button
 * for dormant tasks; expanded and collapsed variants crossfade inside a
 * width-animated container (badge push).
 */
export const TaskEntryPlaySlot: React.FC<TaskEntryPlaySlotProps> = ({
  task, isExpanded, showCollapsedPlay, hidePlay = false,
  expandingFromCollapsed, collapsingFromExpanded,
  reactivateOpen, setReactivateOpen,
  inProjectContext, onDirectWake, onPlay, onRowClick,
}) => {
  const { t } = useTranslation()

  /*
    **Kein Play an einer erledigten Aufgabe.** `hidePlay` setzen bisher nur die
    Seiten, die ausschließlich Erledigtes zeigen (**Geschafft**, der
    Erledigt-Umschalter). In einer normalen Liste blieb der Knopf an einer
    gerade abgehakten Zeile stehen — es gibt daran aber nichts mehr zu
    fokussieren. Ruhende Aufgaben tauschen statt dessen auf „Reaktivieren".
  */
  const noPlay = hidePlay || task.completed

  /*
    **Mit Tooltip samt Kürzel** (Nutzer-Vorgabe, 2026-09-18) — genau wie in den
    Aufgaben-Details, wo derselbe Knopf schon einen trägt. Ein Knopf, der nur
    ein Zeichen zeigt, muss sagen dürfen, was er tut; und `P` ist der Weg
    dorthin ohne Maus (CONTEXT.md, Einstiege).

    Der Knopf nimmt die Ref selbst (`forwardRef`) — in einen zweiten Knopf
    gewickelt stürbe der Klick an seinem `stopPropagation` (gefunden am
    16.09.2026).
  */
  const playButton = (expanded: boolean) => (
    <WithTooltip content={t('taskItem.menu.focus')} shortcut={{ kind: 'character', text: 'P' }}>
      <TaskPlayButton
        size={expanded ? 28 : 24}
        variant={expanded ? 'expanded' : 'default'}
        aria-label={t('taskItem.menu.focus')}
        onClick={() => onPlay?.(task.id)}
      />
    </WithTooltip>
  )

  const reactivateButton = (collapsed: boolean) => (
    <TaskReactivateButton
      task={task}
      ariaLabel={t('dormant.reactivate')}
      // In expanded mode the row already owns selection / edit focus —
      // don't forward onClick, otherwise pressing Reactivate would also
      // collapse the entry (and unmount this slot, killing the dropdown
      // that just opened).
      onClick={collapsed ? (e) => onRowClick?.(task.id, e) : undefined}
      open={reactivateOpen}
      onOpenChange={setReactivateOpen}
      directWake={inProjectContext}
      onDirectWake={onDirectWake}
    />
  )

  return (
    <motion.div
      className="task-entry__play-button"
      animate={{ width: noPlay ? 0 : isExpanded ? (task.dormant ? 24 : 28) : showCollapsedPlay ? 24 : 0 }}
      transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <AnimatePresence>
        {isExpanded && !noPlay && (
          <motion.div
            key="play-expanded"
            className="task-entry__play-slot task-entry__play-slot--expanded"
            initial={expandingFromCollapsed ? { opacity: 0, scale: 0.85 } : false}
            animate={{ opacity: 1, scale: 1 }}
            // Exit matches the bottom-wrapper close (~0.22 s) so the
            // expanded play button fades out in step with the action
            // bar, not 100 ms ahead of it.
            exit={{ opacity: 0, scale: 0.70, transition: { duration: 0.13, ease: 'easeOut' } }}
            transition={{ duration: 0.2, delay: 0.06 }}
          >
            {task.dormant ? reactivateButton(false) : playButton(true)}
          </motion.div>
        )}
        {showCollapsedPlay && !noPlay && !isExpanded && (
          <motion.div
            key="play-collapsed"
            className="task-entry__play-slot task-entry__play-slot--collapsed"
            initial={collapsingFromExpanded ? { opacity: 0 } : { scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            transition={{
              scale: { type: 'spring', stiffness: 380, damping: 22 },
              opacity: { duration: 0.2, delay: 0.06 },
            }}
          >
            {task.dormant ? reactivateButton(true) : playButton(false)}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
